import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from '../assets/img/logo.svg';
import { FaUser } from 'react-icons/fa';

const Navigation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    let ticking = false;

    const isDetailPage = location.pathname.includes('/news/') ||
                        location.pathname.includes('/events/') ||
                        location.pathname.includes('/staff') ||
                        location.pathname.includes('/sponsors') ||
                        location.pathname.includes('/support') ||
                        location.pathname.includes('/privacy');

    const updateNav = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 200 || isDetailPage) {
        setIsScrolled(true);
        setShowNav(true);
      } else {
        setIsScrolled(false);
        setShowNav(false);
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateNav);
        ticking = true;
      }
    };

    updateNav();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const scrollToPartners = () => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollToPartners: true } });
    } else {
      const partnersSection = document.getElementById('partners');
      if (partnersSection) {
        partnersSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navigationElement = (
    <header 
      className={`modern-header ${isScrolled ? 'scrolled' : ''} ${showNav ? 'visible' : 'hidden'}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: '80px',
        zIndex: 999999,
        background: showNav ? 'rgba(0, 35, 54, 0.98)' : 'rgba(0, 35, 54, 0.95)',
        transform: showNav ? 'translateY(0)' : 'translateY(-100%)',
        opacity: showNav ? 1 : 0,
        transition: 'all 0.4s ease',
        borderBottom: showNav ? '2px solid #00cbcd' : 'none',
        backdropFilter: 'blur(20px)'
      }}
    >
      <nav className="navbar navbar-expand-lg">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img 
              src={Logo} 
              alt="Mileage Riot Logo" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://hub.mileageriot.com/assets/images/logo_sm.png';
              }}
            />
          </Link>
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarSupportedContent" 
            aria-controls="navbarSupportedContent" 
            aria-expanded="false" 
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav mx-auto menu">
              <li className="nav-item">
                <NavLink className="nav-link" to="/">{t('nav.home')}</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/news">{t('nav.news')}</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/staff">{t('nav.team')}</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/events">{t('nav.convoys')}</NavLink>
              </li>
                            <li className="nav-item">
                <NavLink className="nav-link" to="/sponsors">{t('nav.sponsors')}</NavLink>
              </li>
              <li className="nav-item">
                <NavLink className="nav-link" to="/support">{t('nav.support')}</NavLink>
              </li>
              <li className="nav-item">
                <button 
                  className="nav-link nav-button" 
                  onClick={scrollToPartners}
                >
                  {t('nav.partners')}
                </button>
              </li>
              <li className="nav-item d-md-none d-block">
                <NavLink className="nav-link" to="https://hub.mileageriot.com/dashboard">{t('nav.login')}</NavLink>
              </li>
            </ul>
            <div className="d-flex align-items-center">
              <LanguageSwitcher />
              <div className="ms-3 d-none d-md-block">
                <Link to="https://hub.mileageriot.com/dashboard" className="btn btn-join" type="submit">
                  <FaUser className='me-1'/> {t('nav.login')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );

  // Use createPortal to render the navigation outside of the normal DOM flow
  return createPortal(navigationElement, document.body);
};

export default Navigation;